// web/src/pages/Terms.tsx
import type { JSX, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const EFFECTIVE_DATE_TEXT = "August 21, 2025";
const LAST_UPDATED_TEXT = "August 21, 2025";
const SUPPORT_EMAIL = "support@spellgather.com";

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-8 mb-3">
        {title}
      </h2>
      <div className="prose prose-invert max-w-none">{children}</div>
    </section>
  );
}

export default function Terms(): JSX.Element {
  return (
    <main className="mx-auto max-w-4xl p-6">
      <Card className="bg-gray-900 border-gray-700 shadow-xl">
        <CardHeader>
          <CardTitle className="text-white">Terms of Service</CardTitle>
          <CardDescription className="text-white/90">
            Effective: {EFFECTIVE_DATE_TEXT} · Last Updated: {LAST_UPDATED_TEXT}
          </CardDescription>
        </CardHeader>

        <CardContent className="text-white">
          {/* Quick links */}
          <nav aria-label="Table of contents" className="mb-6">
            <ul className="text-sm grid sm:grid-cols-2 gap-2">
              <li>
                <a className="underline" href="#introduction">
                  1. Introduction & Acceptance
                </a>
              </li>
              <li>
                <a className="underline" href="#eligibility">
                  2. Eligibility & Accounts
                </a>
              </li>
              <li>
                <a className="underline" href="#user-conduct">
                  3. User Conduct
                </a>
              </li>
              <li>
                <a className="underline" href="#content-license">
                  4. User Content & License
                </a>
              </li>
              <li>
                <a className="underline" href="#offers-transactions">
                  5. Offers, Trades & Purchases
                </a>
              </li>
              <li>
                <a className="underline" href="#safety-minors">
                  6. Safety & Minors
                </a>
              </li>
              <li>
                <a className="underline" href="#privacy">
                  7. Privacy
                </a>
              </li>
              <li>
                <a className="underline" href="#copyright">
                  8. Copyright & DMCA
                </a>
              </li>
              <li>
                <a className="underline" href="#disclaimers">
                  9. Disclaimers
                </a>
              </li>
              <li>
                <a className="underline" href="#liability">
                  10. Limitation of Liability
                </a>
              </li>
              <li>
                <a className="underline" href="#indemnity">
                  11. Indemnity
                </a>
              </li>
              <li>
                <a className="underline" href="#changes">
                  12. Changes to the Service & Terms
                </a>
              </li>
              <li>
                <a className="underline" href="#termination">
                  13. Termination
                </a>
              </li>
              <li>
                <a className="underline" href="#governing">
                  14. Governing Law & Disputes
                </a>
              </li>
              <li>
                <a className="underline" href="#misc">
                  15. Miscellaneous
                </a>
              </li>
              <li>
                <a className="underline" href="#contact">
                  Contact
                </a>
              </li>
            </ul>
          </nav>

          <Section id="introduction" title="1) Introduction & Acceptance">
            <p>
              These Terms of Service (the “Terms”) govern your access to and use
              of Spellgather’s website, mobile experiences, and related services
              (collectively, the “Service”). By creating an account, accessing,
              or using the Service, you agree to be bound by these Terms. If you
              do not agree, you may not use the Service.
            </p>
            <p>
              Spellgather is a community platform for collectible-card players
              to create profiles, showcase collections, follow other players,
              exchange messages, and send/receive offers for trades or
              purchases. Spellgather is not a party to user-to-user transactions
              and does not hold funds or guarantee performance unless expressly
              stated in a separate written policy.
            </p>
          </Section>

          <Section id="eligibility" title="2) Eligibility & Accounts">
            <ul>
              <li>
                <strong>Age.</strong> You must be at least 13 years old to use
                the Service. Users aged 13–17 may use the Service only with the
                consent and supervision of a parent or legal guardian who agrees
                to these Terms on their behalf. Users under 18 may not enter
                into legally binding sales without guardian involvement, and
                must use the Service in accordance with applicable laws.
              </li>
              <li>
                <strong>Account Security.</strong> You are responsible for
                maintaining the confidentiality of your login credentials and
                for all activity under your account. Notify us immediately at{" "}
                <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>{" "}
                of any suspected unauthorized use.
              </li>
              <li>
                <strong>Accurate Information.</strong> You agree to provide and
                maintain accurate information in your profile and settings,
                including your email address for account notices and optional
                notifications.
              </li>
            </ul>
          </Section>

          <Section id="user-conduct" title="3) User Conduct">
            <p>When using the Service, you agree that you will not:</p>
            <ul>
              <li>
                Violate any law or third-party rights (including privacy,
                publicity, and IP rights).
              </li>
              <li>
                Post, share, or transmit content that is illegal, pornographic,
                harassing, hateful, or harmful.
              </li>
              <li>
                Engage in fraud, scams, or deceptive practices in messages,
                offers, or listings.
              </li>
              <li>
                Use automated means to access or scrape the Service without
                permission.
              </li>
              <li>
                Interfere with the Service, attempt to probe or breach security,
                or misuse reporting tools.
              </li>
            </ul>
            <p>
              We may remove content, restrict features, or suspend/terminate
              accounts that violate these Terms or our Community Guidelines.
            </p>
          </Section>

          <Section id="content-license" title="4) User Content & License">
            <ul>
              <li>
                <strong>Your Content.</strong> “User Content” includes text,
                images, profile data, card photos, offers, and messages you
                provide. You retain ownership of your User Content.
              </li>
              <li>
                <strong>License to Spellgather.</strong> You grant Spellgather a
                worldwide, non-exclusive, royalty-free license to host, store,
                reproduce, display, and distribute your User Content solely for
                operating and improving the Service. This license ends when you
                delete the content from the Service, except for (i) reasonable
                backup copies, (ii) content others have already viewed, shared,
                or copied, and (iii) records we must retain to comply with law
                or enforce these Terms.
              </li>
              <li>
                <strong>Card History.</strong> When you add a card to your
                collection, the Service may create a non-sensitive history
                marker (e.g., “added to collection,” “traded to @username,” with
                timestamps) that can follow the item within Spellgather. You
                agree that such system history may be visible to participants in
                a transaction and used to enhance trust and discovery.
              </li>
              <li>
                <strong>Feedback.</strong> If you submit feedback or
                suggestions, you grant Spellgather a perpetual, irrevocable,
                royalty-free license to use it without restriction or
                compensation.
              </li>
            </ul>
          </Section>

          <Section
            id="offers-transactions"
            title="5) Offers, Trades & Purchases"
          >
            <ul>
              <li>
                <strong>Platform Role.</strong> Spellgather facilitates
                discovery and messaging but is not a party to user-to-user
                transactions unless we expressly say otherwise. We do not set
                prices, guarantee condition, or handle payment/shipping unless
                described in a separate feature or policy.
              </li>
              <li>
                <strong>Offers.</strong> Users may send offers on items a
                profile has marked as “for sale” or “for trade.” An offer is not
                binding until both parties explicitly agree in writing (e.g., in
                messages) to the material terms (item(s), condition, price or
                exchange, shipping/pickup, timing).
              </li>
              <li>
                <strong>Local Meetups.</strong> If you arrange to meet, you do
                so at your own risk. Use common-sense safety practices, meet in
                public, and comply with local laws. Spellgather does not perform
                background checks.
              </li>
              <li>
                <strong>Disputes Between Users.</strong> Users are solely
                responsible for resolving disputes arising from their
                transactions. We may provide limited tools (such as reporting or
                blocking) but we do not adjudicate disputes or guarantee
                outcomes.
              </li>
            </ul>
          </Section>

          <Section id="safety-minors" title="6) Safety & Minors">
            <ul>
              <li>
                <strong>13–17 with Guardian Consent.</strong> Features that
                involve trading, purchases, or in-person meetups should be
                supervised by a parent or legal guardian. Guardians should
                review profiles, messages, and offers, and make final decisions
                on transactions and locations.
              </li>
              <li>
                <strong>Reporting.</strong> You can report harmful behavior by
                emailing{" "}
                <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
                . We may restrict features, remove content, or contact
                authorities when legally required.
              </li>
            </ul>
          </Section>

          <Section id="privacy" title="7) Privacy">
            <p>
              Our{" "}
              <Link to="/privacy" className="underline">
                Privacy Policy
              </Link>{" "}
              explains how we collect, use, and share information, including
              optional email notifications for trade/purchase offers, follows,
              and messages. Key points:
            </p>
            <ul>
              <li>
                <strong>Profile Controls.</strong> You can choose whether to
                show your display name, city/state, play formats, meetup
                preferences, and preferred LGS. Some features (e.g., discovery
                or marketplace presence) depend on your setting for availability
                (“Play” and “Trade”).
              </li>
              <li>
                <strong>Location.</strong> If you opt in to use your location to
                find nearby stores, we use it to suggest options; you can turn
                this off at any time.
              </li>
              <li>
                <strong>Email Choices.</strong> You can manage email preferences
                in Settings.
              </li>
            </ul>
            <p>
              For privacy questions, contact{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section id="copyright" title="8) Copyright & DMCA">
            <p>
              We respect intellectual property. If you believe content on the
              Service infringes your copyright, please email a takedown request
              to{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>{" "}
              with subject line: <em>DMCA Takedown Request</em>. Include:
            </p>
            <ul>
              <li>Your contact information.</li>
              <li>
                Identification of the copyrighted work you claim is infringed.
              </li>
              <li>
                Identification of the allegedly infringing material (URL,
                screenshots).
              </li>
              <li>
                A statement that you have a good-faith belief the use is not
                authorized.
              </li>
              <li>
                A statement, under penalty of perjury, that your notice is
                accurate and you are the copyright owner or authorized to act on
                the owner’s behalf.
              </li>
              <li>Your physical or electronic signature.</li>
            </ul>
            <p>
              We may notify the user who posted the content and, where
              appropriate, accept and act on a counter-notice according to
              applicable law.
            </p>
          </Section>

          <Section id="disclaimers" title="9) Disclaimers">
            <p>
              THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM
              EXTENT PERMITTED BY LAW, SPELLGATHER AND ITS AFFILIATES DISCLAIM
              ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
              NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE
              UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT USER CONTENT WILL BE
              ACCURATE OR RELIABLE.
            </p>
            <p>
              USER-TO-USER TRANSACTIONS ARE AT YOUR OWN RISK. SPELLGATHER DOES
              NOT CONTROL OR GUARANTEE THE QUALITY, EXISTENCE, TITLE, OR SAFETY
              OF ITEMS; THE TRUTH OR ACCURACY OF LISTINGS OR PROFILES; OR THE
              ABILITY OF USERS TO COMPLETE A TRANSACTION.
            </p>
          </Section>

          <Section id="liability" title="10) Limitation of Liability">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, SPELLGATHER AND ITS
              AFFILIATES, DIRECTORS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE
              FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY,
              OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFIT, DATA, GOODWILL, OR
              OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO YOUR USE OF
              (OR INABILITY TO USE) THE SERVICE OR ANY USER-TO-USER TRANSACTION.
            </p>
            <p>
              TO THE EXTENT LIABILITY CANNOT BE EXCLUDED, THE TOTAL LIABILITY OF
              SPELLGATHER FOR ANY CLAIM RELATING TO THE SERVICE WILL NOT EXCEED
              THE GREATER OF (A) THE AMOUNT YOU PAID TO SPELLGATHER (IF ANY) FOR
              THE SERVICE IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE
              CLAIM, OR (B) USD $100.
            </p>
          </Section>

          <Section id="indemnity" title="11) Indemnity">
            <p>
              You agree to defend, indemnify, and hold harmless Spellgather and
              its affiliates, directors, employees, and agents from and against
              any claims, liabilities, damages, losses, and expenses (including
              reasonable attorneys’ fees) arising out of or related to: (a) your
              use of the Service; (b) your User Content; (c) your violation of
              these Terms or any law; or (d) your user-to-user transactions or
              meetups.
            </p>
          </Section>

          <Section id="changes" title="12) Changes to the Service & Terms">
            <p>
              We may modify the Service and these Terms from time to time (for
              example, to reflect new features or legal requirements). If we
              make material changes, we will provide notice (e.g., in-app notice
              or email). The updated Terms will be effective on the stated date.
              By continuing to use the Service after changes take effect, you
              accept the updated Terms.
            </p>
          </Section>

          <Section id="termination" title="13) Termination">
            <p>
              You may stop using the Service at any time. We may suspend or
              terminate your access if we believe you have violated these Terms,
              pose a risk to the community, or for other lawful reasons.
              Sections that by their nature should survive termination (e.g.,
              licenses, disclaimers, limitations of liability, indemnity) will
              survive.
            </p>
          </Section>

          <Section id="governing" title="14) Governing Law & Disputes">
            <p>
              These Terms are governed by the laws applicable in your place of
              residence to the extent not preempted by U.S. federal law. Where
              permitted by law, you consent to the courts located in your place
              of residence as the venue for resolving disputes arising out of or
              relating to these Terms or the Service. Nothing here limits any
              mandatory consumer protections that apply in your jurisdiction.
            </p>
          </Section>

          <Section id="misc" title="15) Miscellaneous">
            <ul>
              <li>
                <strong>Severability.</strong> If any provision of these Terms
                is found unenforceable, that provision will be enforced to the
                maximum extent permissible and the remaining provisions will
                remain in full force and effect.
              </li>
              <li>
                <strong>No Waiver.</strong> Our failure to enforce a provision
                is not a waiver of our right to do so later.
              </li>
              <li>
                <strong>Assignment.</strong> You may not assign these Terms
                without our prior written consent. We may assign these Terms as
                part of a merger, acquisition, or sale of assets, or by
                operation of law.
              </li>
              <li>
                <strong>Entire Agreement.</strong> These Terms, together with
                policies linked here (including the{" "}
                <Link to="/privacy" className="underline">
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link to="/data-protection" className="underline">
                  Data Sharing & Protection Statement
                </Link>
                ), constitute the entire agreement between you and Spellgather
                regarding the Service.
              </li>
            </ul>
          </Section>

          <Section id="contact" title="Contact">
            <p>
              Questions about these Terms? Email{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </Section>

          {/* Convenience CTA back to the age acknowledgment flow */}
          <div className="mt-8">
            <Link to="/terms-ack?next=/settings">
              <Button className="bg-purple-600 hover:bg-purple-700">
                Return to Acknowledgement
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

